/**
 * Factory for creating AI SDK provider models based on a provider name.
 *
 * All returned text/chat models implement the same `LanguageModel` interface from
 * the Vercel AI SDK, so they are fully interchangeable when passed to
 * `generateText()`, `streamText()`, etc.
 */
import type { LanguageModel } from "ai";
import { createOpenRouter } from "@openrouter/ai-sdk-provider";
import { createOpenAI } from "@ai-sdk/openai";
import { createGoogleGenerativeAI } from "@ai-sdk/google";
import { createAnthropic } from "@ai-sdk/anthropic";

export type AiProviderName = "openrouter" | "openai" | "google" | "anthropic";

export type GoogleGenerativeAiApiVersion = "v1" | "v1beta";

/**
 * Nano Banana 2 is exposed through the Gemini API as Gemini 3.1 Flash Image.
 * Keep this configurable at the call site, because provider package aliases may
 * differ between Google API / AI SDK versions.
 */
export const NANO_BANANA_2_MODEL = "gemini-3.1-flash-image";

export interface AiProviderFactoryOptions {
  /** Provider identifier (default: "google") */
  provider?: AiProviderName | string;
  /** Model name to instantiate, e.g. "gemini-3-flash-preview" */
  modelName: string;
  /** Base URL for the proxy / API endpoint */
  baseURL: string;
  /** API key (may be a dummy when using a proxy) */
  apiKey?: string;
  /** Optional custom fetch implementation (e.g. for proxy header injection) */
  fetch?: typeof globalThis.fetch;
}

export interface AiImageProviderFactoryOptions extends Omit<AiProviderFactoryOptions, "modelName"> {
  /** Image model name, defaults to Nano Banana 2 / Gemini 3.1 Flash Image. */
  modelName?: string;
  /**
   * Gemini API version. The GA Gemini 3.1 image models use the stable v1
   * generateContent endpoint. Defaults to "v1".
   */
  apiVersion?: GoogleGenerativeAiApiVersion;
}

/**
 * Creates a `LanguageModel` for the given provider.
 *
 * Every provider package (`@openrouter/ai-sdk-provider`, `@ai-sdk/openai`,
 * `@ai-sdk/google`) returns objects that satisfy the AI SDK's `LanguageModel`
 * type (union of `LanguageModelV2 | LanguageModelV3`).  This means the
 * calling code can use the result with `generateText`, `streamText`, etc.
 * without caring which provider is behind it.
 */
export function createAiModel(options: AiProviderFactoryOptions): LanguageModel {
  const {
    provider = "google",
    modelName,
    baseURL,
    apiKey = "dummy-key",
    fetch: customFetch,
  } = options;

  switch (provider) {
    case "openrouter": {
      const openrouter = createOpenRouter({
        baseURL,
        apiKey,
        ...(customFetch ? { fetch: customFetch } : {}),
      });
      return openrouter.chat(modelName);
    }

    case "openai": {
      const openai = createOpenAI({
        baseURL,
        apiKey,
        ...(customFetch ? { fetch: customFetch } : {}),
      });
      // Use .chat() to force Chat Completions API format.
      // openai() alone defaults to Responses API in @ai-sdk/openai v3,
      // which most compatible providers do not support.
      return openai.chat(modelName);
    }

    case "anthropic": {
      const anthropic = createAnthropic({
        baseURL,
        apiKey,
        ...(customFetch ? { fetch: customFetch } : {}),
      });
      return anthropic(modelName);
    }

    case "google":
    default: {
      // Use native Google provider – required for Gemini 2.5+ "thinking" models
      // which need thought_signature handling in multi-step tool calling.
      // The OpenAI-compatible endpoint does NOT support this.
      const google = createGoogleGenerativeAI({
        baseURL,
        apiKey,
        ...(customFetch ? { fetch: customFetch } : {}),
      });
      return google(modelName);
    }
  }
}

/**
 * Creates the multimodal language model used for Gemini image generation and
 * image editing.
 *
 * Gemini image models return generated images as files from `generateText()`.
 * This path also supports one or more reference images. Do not use
 * `google.image(...)` here: that creates an ImageModel for `generateImage()`,
 * which rejects image editing in some @ai-sdk/google versions.
 */
export function createAiImageModel(
  options: AiImageProviderFactoryOptions
): LanguageModel {
  const {
    provider = "google",
    modelName = NANO_BANANA_2_MODEL,
    baseURL,
    apiKey = "dummy-key",
    fetch: customFetch,
    apiVersion = "v1",
  } = options;

  switch (provider) {
    case "google": {
      const normalizedBaseUrl = baseURL.replace(/\/$/, "");
      const versionedBaseUrl = /\/v1(?:beta)?$/.test(normalizedBaseUrl)
        ? normalizedBaseUrl
        : `${normalizedBaseUrl}/${apiVersion}`;

      const google = createGoogleGenerativeAI({
        baseURL: versionedBaseUrl,
        apiKey,
        ...(customFetch ? { fetch: customFetch } : {}),
      });
      return google(modelName);
    }

    default:
      throw new Error(
        `Image generation provider "${provider}" is not supported. ` +
          `Use provider="google" for Nano Banana 2 / Gemini image models.`
      );
  }
}
